import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, DollarSign, Home, Globe2, Users, Building2, 
  TrendingUp, Scale, Lightbulb, Trophy, Heart, FileText,
  Share2, Twitter, Linkedin, Facebook, Link2, Check, Mail
} from 'lucide-react';

const ConferenceTopicPage = () => {
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = "How Athletes' Taxes Can Solve the Affordable Housing Crisis - Quantum Housing Conference";
  const shareDescription = "An innovative solution to the global housing crisis: harnessing the billions of dollars in taxes that athletes and entertainers contribute annually across multiple jurisdictions.";

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareDescription + '\n\n' + shareUrl)}`
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const keyInsights = [
    {
      icon: DollarSign,
      title: 'The Hidden Revenue Stream',
      description: 'International athlete and entertainer taxation systems generate billions annually. The US "jock tax" alone projected $295 million in 2024, while European nations have even more sophisticated systems.'
    },
    {
      icon: Scale,
      title: 'Unique Tax Framework',
      description: 'Unlike regular business travelers protected by the "twelve day rule," athletes and entertainers face immediate tax obligations from day one in every jurisdiction where they perform.'
    },
    {
      icon: Globe2,
      title: 'Global Crisis Scale',
      description: 'UN Habitat reports the world needs to build 96,000 affordable homes every day to house the three billion people who will need adequate housing by 2030.'
    },
    {
      icon: TrendingUp,
      title: 'Rising Housing Costs',
      description: 'Housing prices have risen dramatically: 54% in the US, 32% in China, and 15% in the EU between 2015 and 2024, far outpacing income growth.'
    }
  ];

  const athleteExamples = [
    {
      name: 'LeBron James',
      initiative: 'I PROMISE Housing',
      description: 'A fifty unit affordable housing complex in Akron, Ohio, featuring apartments with fiber optic Wi-Fi and community facilities through the LeBron James Family Foundation.',
      location: 'USA'
    },
    {
      name: 'Taylor Swift',
      initiative: 'Habitat for Humanity',
      description: 'Significant donations to disaster relief and housing security, including $100,000 to Habitat for Humanity and housing assistance for tornado victims.',
      location: 'Global'
    },
    {
      name: 'UK Footballers Legacy Foundation',
      initiative: '£400M Development',
      description: 'Rio Ferdinand, Bobby Zamora, and Mark Noble established a foundation for urban regeneration, with projects providing up to 1,300 homes including social housing.',
      location: 'UK'
    }
  ];

  const globalModels = [
    {
      country: 'Austria',
      title: 'Dedicated Housing Tax',
      description: 'Austrians pay approximately 1% of income as housing tax, funding Gemeindebauten (communal buildings) and rent controlled flats through not for profit developers.'
    },
    {
      country: 'Japan',
      title: 'Form Based Zoning',
      description: 'Centrally regulated, uniform zoning nationwide with streamlined permitting enables far more housing construction, keeping prices affordable even in Tokyo.'
    },
    {
      country: 'Spain',
      title: 'Beckham Law',
      description: 'Foreign players taxed at 24.75% (vs 40%+ standard rates), attracting international talent while generating substantial revenue for the state.'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 mb-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
              <FileText className="w-4 h-4 text-secondary" />
              <span className="text-sm font-medium">Conference Theme</span>
            </span>
            <h1 className="font-outfit text-4xl md:text-6xl font-bold tracking-tight mb-6" data-testid="topic-title">
              How Athletes' Taxes Can Solve the
              <br />
              <span className="text-gradient-housing">Affordable Housing Crisis</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              An innovative solution to the global housing crisis: harnessing the billions of dollars 
              in taxes that athletes and entertainers contribute annually across multiple jurisdictions.
            </p>
          </div>

          <div className="glass-card rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="https://images.unsplash.com/photo-1621062089461-01f1eaebb66c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxjb25maWRlbnQlMjBhZnJpY2FuJTIwYW1lcmljYW4lMjBtYWxlJTIwZXhlY3V0aXZlJTIwcG9ydHJhaXQlMjBzdWl0fGVufDB8fHx8MTc2OTMxNTQ3N3ww&ixlib=rb-4.1.0&q=85"
                alt="Vernand Morency"
                className="w-16 h-16 rounded-full object-cover"
              />
              <div>
                <p className="font-semibold">Vernand Morency</p>
                <p className="text-sm text-muted-foreground">2025 Global Sports Fellow, Harvard University</p>
                <p className="text-sm text-muted-foreground">Former NFL & MLB Professional Athlete</p>
              </div>
            </div>
            <blockquote className="text-lg italic text-muted-foreground border-l-4 border-primary pl-6">
              "In a time of profound global socioeconomic shifts, an unexpected solution to the worldwide 
              affordable housing crisis emerges from the intersection of international sports, taxation, 
              and community development."
            </blockquote>
          </div>
        </div>
      </section>

      {/* Key Insights */}
      <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="key-insights">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              Research Findings
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold">
              Key <span className="text-gradient-quantum">Insights</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {keyInsights.map((insight, index) => (
              <div
                key={index}
                className="glass-card rounded-xl p-6 hover:border-primary/50 transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4">
                  <insight.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-outfit text-xl font-semibold mb-2">{insight.title}</h3>
                <p className="text-muted-foreground">{insight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Vision */}
      <section className="px-6 md:px-12 lg:px-24 py-20 bg-muted/30 mb-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              Mandela's Vision
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-6">
              Sports as a Force for <span className="text-gradient-housing">Good</span>
            </h2>
          </div>
          <div className="glass-card rounded-2xl p-8 md:p-12 text-center">
            <Trophy className="w-16 h-16 text-secondary mx-auto mb-6" />
            <blockquote className="text-xl md:text-2xl italic mb-6">
              "Sport has the power to change the world. It has the power to unite in a way that little else does. 
              It speaks to youth in a language they understand. Sport can create hope where once there was only despair."
            </blockquote>
            <p className="text-muted-foreground">
              — Nelson Mandela, Laureus World Sports Awards, 2000
            </p>
          </div>
        </div>
      </section>

      {/* Athletes Leading the Way */}
      <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="athlete-examples">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              From Philanthropy to Policy
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold">
              Athletes <span className="text-gradient-quantum">Leading the Way</span>
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              High profile athletes and entertainers have created housing initiatives that demonstrate 
              what is possible when resources are directed toward community needs.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {athleteExamples.map((example, index) => (
              <div
                key={index}
                className="glass-card rounded-xl p-6 hover:border-secondary/50 transition-all"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Heart className="w-5 h-5 text-secondary" />
                  <span className="text-sm text-muted-foreground">{example.location}</span>
                </div>
                <h3 className="font-outfit text-xl font-bold mb-1">{example.name}</h3>
                <p className="text-primary font-medium mb-3">{example.initiative}</p>
                <p className="text-muted-foreground text-sm">{example.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Solutions */}
      <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="global-models">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              Global Housing Solutions
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold">
              Models That <span className="text-gradient-housing">Work</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {globalModels.map((model, index) => (
              <div
                key={index}
                className="glass-card rounded-xl p-6 border-t-4 border-t-primary"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Globe2 className="w-5 h-5 text-primary" />
                  <span className="font-semibold">{model.country}</span>
                </div>
                <h3 className="font-outfit text-lg font-semibold mb-2">{model.title}</h3>
                <p className="text-muted-foreground text-sm">{model.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10" />
            <div className="relative z-10">
              <Lightbulb className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
                The Path Forward
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                The call for international athlete tax transparency is not just about following the money 
                across borders—it is about reimagining the relationship between our global cultural heroes 
                and the worldwide communities they serve.
              </p>
              <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join us at the Global Affordable Housing Summit to explore how this innovative approach 
                can transform housing policy through coordination with the UN and G20.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
                >
                  Register for Summit
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/nexus-impact"
                  className="inline-flex items-center gap-2 px-8 py-4 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
                >
                  Explore NexusImpact.AI
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Sharing */}
      <section className="px-6 md:px-12 lg:px-24 mb-12" data-testid="social-sharing">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-2xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <Share2 className="w-6 h-6 text-primary" />
                <div>
                  <h3 className="font-outfit font-semibold">Share This Research</h3>
                  <p className="text-sm text-muted-foreground">Help spread awareness about this innovative solution</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={shareLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#1DA1F2]/20 hover:bg-[#1DA1F2]/30 flex items-center justify-center transition-colors"
                  title="Share on Twitter"
                  data-testid="share-twitter"
                >
                  <Twitter className="w-5 h-5 text-[#1DA1F2]" />
                </a>
                <a
                  href={shareLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#0A66C2]/20 hover:bg-[#0A66C2]/30 flex items-center justify-center transition-colors"
                  title="Share on LinkedIn"
                  data-testid="share-linkedin"
                >
                  <Linkedin className="w-5 h-5 text-[#0A66C2]" />
                </a>
                <a
                  href={shareLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#1877F2]/20 hover:bg-[#1877F2]/30 flex items-center justify-center transition-colors"
                  title="Share on Facebook"
                  data-testid="share-facebook"
                >
                  <Facebook className="w-5 h-5 text-[#1877F2]" />
                </a>
                <a
                  href={shareLinks.email}
                  className="w-10 h-10 rounded-full bg-secondary/20 hover:bg-secondary/30 flex items-center justify-center transition-colors"
                  title="Share via Email"
                  data-testid="share-email"
                >
                  <Mail className="w-5 h-5 text-secondary" />
                </a>
                <button
                  onClick={copyToClipboard}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    copied 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                  }`}
                  title={copied ? 'Copied!' : 'Copy link'}
                  data-testid="copy-link"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Source Attribution */}
      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            Based on research by Vernand Morency, published at the 
            <a 
              href="https://epicenter.wcfia.harvard.edu/article/how-athletes-taxes-can-solve-affordable-housing-crisis"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline ml-1"
            >
              Weatherhead Center for International Affairs, Harvard University
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default ConferenceTopicPage;
